import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

declare const PayU: (merchantId: string) => { secureForms: (options?: { lang?: string }) => any; tokenize: (type?: string) => Promise<any> };

type PayuSecureFormSelectors = {
  card?: string;
  number?: string;
  date?: string;
  cvv?: string;
};

@Injectable({
  providedIn: 'root'
})
export class PayuExpressService {
  private payuSdk: { secureForms: (options?: { lang?: string }) => any; tokenize: (type?: string) => Promise<any> } | null = null;
  private secureCardForms: Array<{ remove: () => void }> = [];

  public constructor(private translateService: TranslateService) {}

  public initializeSecureCardForm(merchantId: string, selectors: PayuSecureFormSelectors): void {
    if (this.secureCardForms.length > 0) {
      return;
    }

    if (typeof PayU !== 'function') {
      throw new Error('PayU SDK is not available');
    }

    this.payuSdk = PayU(merchantId);
    const lang = this.getPayuLang();
    const secureForms = this.payuSdk.secureForms(lang ? { lang } : undefined);

    if (selectors.card) {
      const cardForm = secureForms.add('card', {
        placeholder: {
          date: 'MM / YY'
        }
      });
      cardForm.render(selectors.card);
      this.secureCardForms = [cardForm];
      return;
    }

    if (!selectors.number || !selectors.date || !selectors.cvv) {
      throw new Error('PayU SecureForms selectors are incomplete');
    }

    const numberForm = secureForms.add('number');
    const dateForm = secureForms.add('date', {
      placeholder: {
        date: 'MM / YY'
      }
    });
    const cvvForm = secureForms.add('cvv');

    numberForm.render(selectors.number);
    dateForm.render(selectors.date);
    cvvForm.render(selectors.cvv);

    this.secureCardForms = [numberForm, dateForm, cvvForm];
  }

  public destroySecureCardForm(): void {
    this.secureCardForms.forEach((form) => form.remove());
    this.secureCardForms = [];
    this.payuSdk = null;
  }

  public getCardToken(merchantId: string, selectors: PayuSecureFormSelectors): Observable<any> {
    return new Observable<any>((subscriber) => {
      if (!this.payuSdk || this.secureCardForms.length === 0) {
        try {
          this.initializeSecureCardForm(merchantId, selectors);
        } catch (error) {
          subscriber.error(error);
          subscriber.complete();
          return;
        }
      }

      const payuSdk = this.payuSdk;
      if (!payuSdk) {
        subscriber.error({ message: 'PayU SDK is not initialized.' });
        subscriber.complete();
        return;
      }
      payuSdk
        .tokenize('SINGLE')
        .then((result: { status?: string; body?: { token?: string }; error?: unknown }) => {
          if (result.status === 'SUCCESS' && result.body?.token) {
            subscriber.next(result.body);
          } else {
            subscriber.error(result.error ?? { message: 'Tokenization failed.' });
          }
          subscriber.complete();
        })
        .catch((error: unknown) => {
          subscriber.error(error);
          subscriber.complete();
        });
    });
  }

  public extractErrorMessages(error: unknown): Array<string> {
    const fallbackError = 'Invalid card data.';

    if (!error || typeof error !== 'object') {
      return [fallbackError];
    }

    const candidate = error as {
      messages?: Array<{ message?: string }>;
      error?: { messages?: Array<{ message?: string }> };
      message?: string;
    };

    if (candidate.error?.messages?.length) {
      return candidate.error.messages.map((msg) => msg.message).filter((msg): msg is string => !!msg?.trim());
    }

    if (candidate.messages?.length) {
      return candidate.messages.map((msg) => msg.message).filter((msg): msg is string => !!msg?.trim());
    }

    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return [candidate.message];
    }

    if (error instanceof Error && error.message) {
      return [error.message];
    }

    return [fallbackError];
  }

  private getPayuLang(): string | null {
    const translateLang = this.translateService.currentLang || this.translateService.defaultLang;
    if (translateLang) {
      return translateLang;
    }

    try {
      return localStorage.getItem('lang') || localStorage.getItem('language') || localStorage.getItem('currentLang');
    } catch {
      return null;
    }
  }
}
